import React, {useState} from 'react';
import {Button, Checkbox, Chip, Divider, ListItemText, Menu, MenuItem, Stack} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface FilterMenuChipProps {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}

// A chip that opens a multi-select menu; the label shows how many are selected ("Equipment · 2").
const FilterMenuChip = ({label, options, value, onChange}: FilterMenuChipProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const active = value.length > 0;
  // A count, not the names: "Container/Chassis (40'/45'/53')" widened the chip until the row
  // wrapped under the open menu. The menu shows what's checked.
  const chipLabel = active ? `${label} · ${value.length}` : label;

  // The arrow (delete icon) opens the menu too; anchor on the chip, not the icon.
  const handleOpen = (e: React.SyntheticEvent<HTMLElement>) => {
    setAnchorEl((e.currentTarget.closest('.MuiChip-root') as HTMLElement) ?? e.currentTarget);
  };

  const toggle = (option: string) => onChange(value.includes(option) ? value.filter(v => v !== option) : [...value, option]);

  return (
    <>
      <Chip
        label={chipLabel}
        onClick={handleOpen}
        onDelete={handleOpen}
        deleteIcon={<ArrowDropDownIcon />}
        color={active ? 'primary' : 'default'}
        variant={active ? 'filled' : 'outlined'}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{fontWeight: 500}}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} MenuListProps={{dense: true}}>
        {options.map(option => (
          <MenuItem key={option} onClick={() => toggle(option)} sx={{pr: 3}}>
            <Checkbox size="small" checked={value.includes(option)} sx={{p: 0.5, mr: 1}}/>
            <ListItemText primary={option}/>
          </MenuItem>
        ))}
        <Divider/>
        <Stack direction="row" justifyContent="space-between" sx={{px: 1.5, pt: 0.5}}>
          <Button size="small" onClick={() => onChange([])}>Clear</Button>
          {/* SecurSpace orange; the theme's secondary is indigo */}
          <Button size="small" variant="contained" disableElevation onClick={() => setAnchorEl(null)}
                  sx={{borderRadius: 999, bgcolor: 'brandOrange.main', '&:hover': {bgcolor: 'brandOrange.dark'}}}>
            Done
          </Button>
        </Stack>
      </Menu>
    </>
  );
};

export default FilterMenuChip;
